import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { fetchProducts } from '../lib/api';
import { TESTIMONIALS } from '../mock';
import { SlidersHorizontal, ChevronDown, Star, BadgeCheck } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SUBCATEGORY_CONTENT } from '../subcategoryContent';

/* Full-bleed hero banner config per top-level category. */
const COLLECTION_BANNERS = {
  earrings: {
    image: 'https://images.unsplash.com/photo-1763628212781-7d52844cf358?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHx3b21hbiUyMGVhcnJpbmdzfGVufDB8fHx8MTc4NDkxMjUyNHww&ixlib=rb-4.1.0&q=85&w=2000',
    tagline: 'From everyday studs to statement hoops — crafted in certified lab diamonds.'
  },
  necklaces: {
    image: 'https://images.unsplash.com/photo-1611652022419-a9419f74343d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHx3b21hbiUyMGRpYW1vbmQlMjBuZWNrbGFjZXxlbnwwfHx8fDE3ODQ5MTI1MTh8MA&ixlib=rb-4.1.0&q=85&w=2000',
    tagline: 'Pendants, mangalsutra, trinkets — close to your heart, always.'
  },
  bracelets: {
    image: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBkaWFtb25kJTIwamV3ZWxsZXJ5fGVufDB8fHx8MTc4NDkxMjQ2NXww&ixlib=rb-4.1.0&q=85&w=2000',
    tagline: 'Tennis, station, and stackable — built for every wrist.'
  }
};

const AVAILABILITY_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'in-stock', label: 'In Stock' },
  { value: 'made-to-order', label: 'Made to Order' }
];

const SORT_OPTIONS = [
  { value: 'low', label: 'Price: Low to High' },
  { value: 'high', label: 'Price: High to Low' },
];

const Collection = () => {
  const { slug, subSlug } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('low');
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [availability, setAvailability] = useState('all');
  const [maxPrice, setMaxPrice] = useState(250000);

  const categoryKey = useMemo(() => {
    return slug?.includes('earring') ? 'earrings'
      : slug?.includes('bracelet') ? 'bracelets'
      : slug?.includes('necklace') ? 'necklaces'
      : null;
  }, [slug]);

  const { title, contentKey } = useMemo(() => {
    const parent = categoryKey ? categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1) : null;
    if (categoryKey && subSlug) {
      const subName = subSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return { title: subName, parentTitle: parent, contentKey: subSlug };
    }
    if (categoryKey) return { title: parent, parentTitle: null, contentKey: `all-${categoryKey}` };
    return { title: 'All Jewellery', parentTitle: null, contentKey: null };
  }, [categoryKey, subSlug]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    const params = { isPublished: 'true', limit: 100 };
    if (categoryKey) params.categorySlug = categoryKey;
    if (subSlug) params.subcategorySlug = subSlug;

    fetchProducts(params).then(data => {
      if (isMounted) {
        setProducts(data);
        setLoading(false);
      }
    });

    return () => { isMounted = false; };
  }, [categoryKey, subSlug]);

  const content = contentKey ? SUBCATEGORY_CONTENT[contentKey] : null;
  const relevantReviews = TESTIMONIALS.filter(t => products.some(p => p.name === t.product)).length
    ? TESTIMONIALS.filter(t => products.some(p => p.name === t.product))
    : TESTIMONIALS;

  const filtered = products
    .filter(p => availability === 'all' || (availability === 'in-stock' ? p.inStock : !p.inStock))
    .filter(p => p.price <= maxPrice);

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'low') return a.price - b.price;
    if (sort === 'high') return b.price - a.price;
    return 0;
  });

  const activeFilterCount = (availability !== 'all' ? 1 : 0) + (maxPrice < 250000 ? 1 : 0);
  const resetFilters = () => { setAvailability('all'); setMaxPrice(250000); };

  return (
    <main className="bg-[#FFFFFF] min-h-screen">
      {/* Hero banner */}
      {categoryKey && COLLECTION_BANNERS[categoryKey] ? (
        <div className="relative w-full h-[52vh] lg:h-[62vh] overflow-hidden">
          <img
            src={COLLECTION_BANNERS[categoryKey].image}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-8 lg:bottom-12 left-6 lg:left-12 text-white max-w-md">
            <h1 className="font-serif text-4xl lg:text-6xl leading-tight">{title}</h1>
            <p className="mt-3 text-[13px] lg:text-[14px] text-white/80 leading-relaxed">
              {COLLECTION_BANNERS[categoryKey].tagline}
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-10">
          <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#1a1a1a]">{title}</h1>
          <p className="font-serif text-base text-[#7a6a54] mt-2">Made to move with you</p>
        </div>
      )}

      {/* Filter & Sort banner */}
      <div className="border-y border-[#e5ddce]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 flex justify-between items-center py-4">
          <button
            onClick={() => setFilterOpen(true)}
            className="flex items-center gap-2 text-[11px] tracking-widest-2 uppercase text-[#1a1a1a] hover:text-[#999959] transition-colors"
          >
            <SlidersHorizontal size={14} strokeWidth={1.6} /> Filter
            {activeFilterCount > 0 && <span className="ml-0.5 text-[#999959]">({activeFilterCount})</span>}
          </button>
          <div className="relative">
            <button
              onClick={() => setSortOpen(o => !o)}
              className="flex items-center gap-1.5 text-[11px] tracking-widest-2 uppercase text-[#1a1a1a] hover:text-[#999959] transition-colors"
            >
              Sort
              <ChevronDown size={12} strokeWidth={1.6} className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-[#e5ddce] z-50 shadow-sm min-w-[190px]">
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSort(opt.value); setSortOpen(false); }}
                    className={`block w-full text-left px-4 py-3 text-[11px] tracking-widest-2 uppercase transition-colors hover:bg-[#f7f4ee] ${sort === opt.value ? 'text-[#999959]' : 'text-[#1a1a1a]'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter drawer */}
      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="right" className="bg-[#FFFFFF] w-[85vw] max-w-[340px] flex flex-col">
          <SheetTitle className="font-serif text-xl text-[#1a1a1a] mb-8 pr-8">Filter</SheetTitle>
          <SheetDescription className="sr-only">Filter collection products by availability and price</SheetDescription>

          <div className="mb-8">
            <div className="text-[11px] tracking-widest-2 uppercase text-[#7a6a54] mb-3">Availability</div>
            <div className="flex flex-col gap-2">
              {AVAILABILITY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setAvailability(opt.value)}
                  className={`text-left px-4 py-2.5 text-[13px] transition-colors ${availability === opt.value ? 'bg-[#999959] text-[#FFFFFF]' : 'bg-[#f7f4ee] text-[#1a1a1a] hover:bg-[#efe9db]'}`}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <div className="text-[11px] tracking-widest-2 uppercase text-[#7a6a54] mb-3">Max Price · ₹{maxPrice.toLocaleString('en-IN')}</div>
            <input
              type="range"
              min="55000"
              max="250000"
              step="5000"
              value={maxPrice}
              onChange={e => setMaxPrice(Number(e.target.value))}
              className="w-full accent-[#999959]"
            />
          </div>

          <div className="mt-auto flex gap-3">
            <button onClick={resetFilters} className="flex-1 border border-[#d8ceb9] text-[11px] tracking-widest-2 uppercase py-3 hover:border-[#1a1a1a] transition-colors">Reset</button>
            <button onClick={() => setFilterOpen(false)} className="flex-1 bg-[#999959] text-[#FFFFFF] text-[11px] tracking-widest-2 uppercase py-3 hover:bg-[#7d8449] transition-colors">Apply</button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Grid */}
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        {loading ? (
          <div className="text-center py-16 text-[#7a6a54] text-sm font-serif">Loading collection products...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16 text-[#7a6a54] text-sm">No products match your filters.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {sorted.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>

      {/* Reviews */}
      {categoryKey && (
        <section className="border-t border-[#e5ddce] py-12 lg:py-16 bg-[#f7f4ee]">
          <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#1a1a1a] mb-8 text-center">What Our Customers Say</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relevantReviews.slice(0, 6).map((t, i) => (
                <div key={i} className="bg-[#FFFFFF] p-6 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <Star key={j} size={14} strokeWidth={0} className={j < t.rating ? 'fill-[#999959] text-[#999959]' : 'fill-[#e5ddce] text-[#e5ddce]'} />
                      ))}
                    </div>
                    {t.verified && (
                      <div className="flex items-center gap-1 text-[10px] tracking-widest-2 uppercase text-[#7a6a54]">
                        <BadgeCheck size={13} strokeWidth={1.6} className="text-[#999959]" />
                        Verified
                      </div>
                    )}
                  </div>
                  <p className="text-[14px] text-[#1a1a1a] leading-relaxed flex-1">"{t.quote}"</p>
                  <div className="flex items-end justify-between gap-3 pt-2 border-t border-[#e5ddce]">
                    <div>
                      <div className="text-[13px] font-medium text-[#1a1a1a]">{t.name}</div>
                      <div className="text-[11px] tracking-widest-2 uppercase text-[#7a6a54] mt-0.5">{t.location}</div>
                    </div>
                    {t.product && <div className="text-[11px] text-[#7a6a54] text-right">{t.product}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {content?.faqs && (
        <section className="border-t border-[#e5ddce] py-12 lg:py-16">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#1a1a1a] mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {content.faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#d8ceb9]">
                  <AccordionTrigger className="font-serif text-[15px] text-left text-[#1a1a1a] py-4 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[13px] text-[#5a5346] leading-relaxed pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* SEO body content */}
      {content?.seo && (
        <section className="border-t border-[#e5ddce] py-12 lg:py-16">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#1a1a1a] mb-6">{content.seo.heading}</h2>
            <p className="text-[14px] text-[#5a5346] leading-relaxed">{content.seo.intro}</p>

            {content.seo.sections.map((s, i) => (
              <div key={i} className="mt-8">
                <h3 className="font-serif text-lg lg:text-xl text-[#1a1a1a] mb-2">{s.title}</h3>
                <p className="text-[14px] text-[#5a5346] leading-relaxed">{s.body}</p>
              </div>
            ))}

            <div className="mt-8">
              <h3 className="font-serif text-lg lg:text-xl text-[#1a1a1a] mb-3">Why House of Seya Stands Out</h3>
              <ul className="space-y-3">
                {content.seo.whyUs.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-[14px] text-[#5a5346] leading-relaxed">
                    <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#c9a86b] flex-shrink-0" />
                    <span><span className="text-[#1a1a1a] font-medium">{point.title}</span> {point.body}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <h3 className="font-serif text-lg lg:text-xl text-[#1a1a1a] mb-2">{content.seo.closingTitle}</h3>
              {content.seo.closing.split('\n\n').map((para, i) => (
                <p key={i} className="text-[14px] text-[#5a5346] leading-relaxed mt-2 first:mt-0">{para}</p>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Collection;
